import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Filter, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { reportsService } from '../../../services/reports.service'
import type {
  InventoryReportFilterOptions,
  InventoryReportFilters,
  InventoryReportResult,
  InventoryReportType,
} from '../../../services/reports.service'
import { CatalogSelect } from '../../ui/CatalogSelect'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { GlobalPaginator } from '../../ui/global-paginator'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'

const REPORT_TABS: Array<{ value: InventoryReportType; label: string }> = [
  { value: 'stocks', label: 'Existencias' },
  { value: 'min-max', label: 'Mínimos y máximos' },
  { value: 'cofepris', label: 'Cofepris' },
  { value: 'batches', label: 'Lotes' },
  { value: 'kardex', label: 'Kardex de productos' },
]

const DEFAULT_FILTERS: Record<InventoryReportType, InventoryReportFilters> = {
  stocks: { stock_status: 'all', show_utilities: false },
  'min-max': {},
  cofepris: {},
  batches: { batch_status: 'all' },
  kardex: { document: 'all' },
}

type Row = Record<string, string | number | null>

interface Column {
  key: string
  label: string
  kind?:
    | 'money'
    | 'percentage'
    | 'quantity'
    | 'integer'
    | 'date'
    | 'date-time'
}

const REPORT_TABLE_HEAD_CLASS =
  'h-auto px-6 py-3 text-xs font-medium uppercase tracking-wider text-table-header-foreground'
const REPORT_TABLE_CELL_CLASS = 'px-6 py-4 text-table-foreground'

const BASE_COLUMNS: Record<InventoryReportType, Column[]> = {
  stocks: [
    { key: 'product', label: 'Producto' },
    { key: 'cost_without_taxes', label: 'Costo sin impuesto', kind: 'money' },
    { key: 'cost_with_taxes', label: 'Costo con impuesto', kind: 'money' },
    { key: 'sale_price', label: 'Precio base', kind: 'money' },
    { key: 'final_sale_price', label: 'Precio final de venta', kind: 'money' },
    { key: 'stock', label: 'Existencia', kind: 'integer' },
    { key: 'unit', label: 'Unidad' },
    { key: 'retail_total', label: 'Costo total', kind: 'money' },
  ],
  'min-max': [
    { key: 'product', label: 'Producto' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'department', label: 'Departamento' },
    { key: 'category', label: 'Categoría' },
    { key: 'stock', label: 'Existencia', kind: 'integer' },
    { key: 'minimum', label: 'Mínimo', kind: 'integer' },
    { key: 'maximum', label: 'Máximo', kind: 'integer' },
  ],
  cofepris: [
    { key: 'sale_date', label: 'Fecha de venta', kind: 'date-time' },
    { key: 'regulatory_folio', label: 'Folio regulatorio' },
    { key: 'regulatory_class', label: 'Clase' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'ticket', label: 'Ticket' },
    { key: 'product', label: 'Producto' },
    { key: 'barcode', label: 'Código de barras' },
    { key: 'quantity', label: 'Cantidad', kind: 'quantity' },
    { key: 'stock_after', label: 'Existencia posterior', kind: 'integer' },
    { key: 'unit', label: 'Unidad' },
    { key: 'batch', label: 'Lote' },
    { key: 'expiration_date', label: 'Caducidad', kind: 'date' },
    { key: 'prescription_date', label: 'Fecha receta', kind: 'date' },
    { key: 'fulfillment_type', label: 'Surtimiento' },
    { key: 'doctor', label: 'Médico' },
    { key: 'professional_license', label: 'Cédula' },
    { key: 'doctor_address', label: 'Domicilio' },
    { key: 'status', label: 'Estado' },
  ],
  batches: [
    { key: 'product', label: 'Producto' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'batch', label: 'Lote' },
    { key: 'expiration_date', label: 'Caducidad', kind: 'date' },
    { key: 'initial_stock', label: 'Existencia inicial', kind: 'integer' },
    { key: 'current_stock', label: 'Existencia actual', kind: 'integer' },
    { key: 'last_exit_at', label: 'Último egreso', kind: 'date-time' },
    { key: 'last_entry_at', label: 'Último ingreso', kind: 'date-time' },
    { key: 'purchase_cost', label: 'Costo almacenado', kind: 'money' },
    { key: 'status', label: 'Estado' },
  ],
  kardex: [
    { key: 'date', label: 'Fecha' },
    { key: 'branch', label: 'Sucursal' },
    { key: 'product', label: 'Producto' },
    { key: 'document', label: 'Documento' },
    { key: 'reference', label: 'Referencia' },
    { key: 'reason', label: 'Motivo' },
    { key: 'batch', label: 'Lote' },
    { key: 'entry', label: 'Entrada', kind: 'integer' },
    { key: 'exit', label: 'Salida', kind: 'integer' },
    { key: 'stock_before', label: 'Existencia anterior', kind: 'integer' },
    { key: 'stock_after', label: 'Existencia posterior', kind: 'integer' },
    { key: 'user', label: 'Usuario' },
  ],
}

export interface AppliedInventoryReportContext {
  type: InventoryReportType
  filters: InventoryReportFilters
}

interface InventarioTabProps {
  activeReport: InventoryReportType
  onActiveReportChange: (type: InventoryReportType) => void
  onAppliedContextChange: (context: AppliedInventoryReportContext) => void
}

export function InventarioTab({
  activeReport,
  onActiveReportChange,
  onAppliedContextChange,
}: InventarioTabProps) {
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [pages, setPages] = useState<Record<InventoryReportType, number>>({
    stocks: 1,
    'min-max': 1,
    cofepris: 1,
    batches: 1,
    kardex: 1,
  })
  const optionsQuery = useQuery({
    queryKey: ['reports', 'inventory', 'filter-options'],
    queryFn: () => reportsService.getInventoryFilterOptions(),
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    onAppliedContextChange({
      type: activeReport,
      filters: filters[activeReport],
    })
  }, [activeReport, filters, onAppliedContextChange])

  const commonProps: InventorySubreportProps = {
    filters: filters[activeReport],
    page: pages[activeReport],
    options: optionsQuery.data,
    optionsLoading: optionsQuery.isLoading,
    onFiltersChange: (nextFilters) => {
      setFilters((current) => ({
        ...current,
        [activeReport]: nextFilters,
      }))
      setPages((current) => ({ ...current, [activeReport]: 1 }))
    },
    onClearFilters: () => {
      setFilters((current) => ({
        ...current,
        [activeReport]: DEFAULT_FILTERS[activeReport],
      }))
      setPages((current) => ({ ...current, [activeReport]: 1 }))
    },
    onPageChange: (page) =>
      setPages((current) => ({ ...current, [activeReport]: page })),
  }

  const handleReportChange = (value: string) => {
    const nextReport = value as InventoryReportType
    if (nextReport === activeReport) return

    setFilters((current) => ({
      ...current,
      [activeReport]: DEFAULT_FILTERS[activeReport],
      [nextReport]: DEFAULT_FILTERS[nextReport],
    }))
    setPages((current) => ({
      ...current,
      [activeReport]: 1,
      [nextReport]: 1,
    }))
    onActiveReportChange(nextReport)
  }

  return (
    <TabsContent value="inventario" className="space-y-5">
      <Tabs
        value={activeReport}
        onValueChange={handleReportChange}
        className="space-y-5"
      >
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {REPORT_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="text-xs sm:text-sm"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <InventoryReportPanel
          key={activeReport}
          {...commonProps}
          type={activeReport}
        />
      </Tabs>
    </TabsContent>
  )
}

interface InventorySubreportProps {
  filters: InventoryReportFilters
  page: number
  options?: InventoryReportFilterOptions
  optionsLoading: boolean
  onFiltersChange: (filters: InventoryReportFilters) => void
  onClearFilters: () => void
  onPageChange: (page: number) => void
}

function InventoryReportPanel({
  type,
  filters,
  page,
  options,
  optionsLoading,
  onFiltersChange,
  onClearFilters,
  onPageChange,
}: InventorySubreportProps & { type: InventoryReportType }) {
  const queryFilters = useMemo(() => ({ ...filters, page }), [filters, page])
  const reportQuery = useQuery({
    queryKey: ['reports', 'inventory', type, queryFilters],
    queryFn: () => reportsService.getInventoryReport<Row>(type, queryFilters),
    placeholderData: (previous) => previous,
  })
  const supportsDates = type === 'cofepris' || type === 'kardex'
  const activeFilters = hasActiveFilters(type, filters)

  return (
    <TabsContent value={type} className="space-y-5">
      <GlobalFilterBar
        show={supportsDates ? ['dates'] : []}
        values={{
          startDate: String(filters.date_from ?? ''),
          endDate: String(filters.date_to ?? ''),
        }}
        onChange={(changes) => {
          const nextFilters = { ...filters }
          if (changes.startDate !== undefined) {
            nextFilters.date_from = changes.startDate || undefined
          }
          if (changes.endDate !== undefined) {
            nextFilters.date_to = changes.endDate || undefined
          }
          onFiltersChange(nextFilters)
        }}
        onClearFilters={onClearFilters}
        hasActiveFilters={activeFilters}
      >
        <InventoryFilters
          type={type}
          filters={filters}
          options={options}
          onChange={onFiltersChange}
        />
        {optionsLoading && (
          <span className="flex items-center gap-1 self-center text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Catálogos
          </span>
        )}
        {!supportsDates && activeFilters && (
          <div className="flex min-h-10 items-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="h-7 rounded-full text-xs"
            >
              <Filter className="mr-1 h-3 w-3" /> Limpiar
            </Button>
          </div>
        )}
      </GlobalFilterBar>

      {type === 'cofepris' && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Bitácora operativa de la información capturada por el sistema; no
          constituye un formato legal certificado.
        </p>
      )}

      <InventoryResults
        type={type}
        filters={filters}
        result={reportQuery.data}
        isLoading={reportQuery.isLoading}
        isFetching={reportQuery.isFetching}
        error={reportQuery.error}
        onPageChange={onPageChange}
      />
    </TabsContent>
  )
}

function InventoryFilters({
  type,
  filters,
  options,
  onChange,
}: {
  type: InventoryReportType
  filters: InventoryReportFilters
  options?: InventoryReportFilterOptions
  onChange: (filters: InventoryReportFilters) => void
}) {
  if (type === 'stocks') {
    return (
      <>
        <OptionsSelect
          label="Sucursal"
          value={filters.sucursal_id}
          options={options?.sucursals}
          onChange={(value) =>
            onChange({ ...filters, sucursal_id: numberOrUndefined(value) })
          }
        />
        <ProductFilter filters={filters} onChange={onChange} />
        <OptionsSelect
          label="Existencia"
          value={filters.stock_status ?? 'all'}
          options={options?.stock_statuses}
          allowAll={false}
          onChange={(value) =>
            onChange({
              ...filters,
              stock_status: value as InventoryReportFilters['stock_status'],
            })
          }
        />
        <BooleanFilter
          label="Mostrar utilidades"
          checked={Boolean(filters.show_utilities)}
          onChange={(checked) =>
            onChange({ ...filters, show_utilities: checked })
          }
        />
      </>
    )
  }
  if (type === 'min-max') {
    return (
      <>
        <OptionsSelect
          label="Sucursal"
          value={filters.sucursal_id}
          options={options?.sucursals}
          onChange={(value) =>
            onChange({ ...filters, sucursal_id: numberOrUndefined(value) })
          }
        />
        <OptionsSelect
          label="Departamento"
          value={filters.department_id}
          options={options?.departments}
          onChange={(value) =>
            onChange({ ...filters, department_id: numberOrUndefined(value) })
          }
        />
        <OptionsSelect
          label="Categoría"
          value={filters.category_id}
          options={options?.categories}
          onChange={(value) =>
            onChange({ ...filters, category_id: numberOrUndefined(value) })
          }
        />
      </>
    )
  }
  if (type === 'cofepris') {
    return (
      <>
        <OptionsSelect
          label="Sucursal"
          value={filters.sucursal_id}
          options={options?.sucursals}
          onChange={(value) =>
            onChange({ ...filters, sucursal_id: numberOrUndefined(value) })
          }
        />
        <Field label="Ticket">
          <Input
            value={filters.ticket ?? ''}
            placeholder="Buscar ticket"
            maxLength={80}
            onChange={(event) =>
              onChange({
                ...filters,
                ticket: event.target.value || undefined,
              })
            }
          />
        </Field>
        <ProductFilter filters={filters} onChange={onChange} />
        <OptionsSelect
          label="Clase"
          value={filters.regulatory_class}
          options={options?.regulatory_classes}
          onChange={(value) =>
            onChange({
              ...filters,
              regulatory_class:
                value === 'all'
                  ? undefined
                  : (value as InventoryReportFilters['regulatory_class']),
            })
          }
        />
        <OptionsSelect
          label="Surtimiento"
          value={filters.fulfillment_type}
          options={options?.fulfillment_types}
          onChange={(value) =>
            onChange({
              ...filters,
              fulfillment_type:
                value === 'all'
                  ? undefined
                  : (value as InventoryReportFilters['fulfillment_type']),
            })
          }
        />
        <OptionsSelect
          label="Médico"
          value={filters.doctor}
          options={options?.doctors}
          onChange={(value) =>
            onChange({
              ...filters,
              doctor: value === 'all' ? undefined : value,
            })
          }
        />
      </>
    )
  }
  if (type === 'batches') {
    return (
      <>
        <OptionsSelect
          label="Sucursal"
          value={filters.sucursal_id}
          options={options?.sucursals}
          onChange={(value) =>
            onChange({ ...filters, sucursal_id: numberOrUndefined(value) })
          }
        />
        <ProductFilter filters={filters} onChange={onChange} />
        <OptionsSelect
          label="Estado"
          value={filters.batch_status ?? 'all'}
          options={options?.batch_statuses}
          allowAll={false}
          onChange={(value) =>
            onChange({
              ...filters,
              batch_status: value as InventoryReportFilters['batch_status'],
            })
          }
        />
      </>
    )
  }
  return (
    <>
      <OptionsSelect
        label="Sucursal"
        value={filters.sucursal_id}
        options={options?.sucursals}
        onChange={(value) =>
          onChange({ ...filters, sucursal_id: numberOrUndefined(value) })
        }
      />
      <ProductFilter filters={filters} onChange={onChange} />
      <OptionsSelect
        label="Documentos"
        value={filters.document ?? 'all'}
        options={options?.documents}
        allowAll={false}
        onChange={(value) =>
          onChange({
            ...filters,
            document: value as InventoryReportFilters['document'],
          })
        }
      />
      <OptionsSelect
        label="Usuario"
        value={filters.user_id}
        options={options?.users}
        onChange={(value) =>
          onChange({ ...filters, user_id: numberOrUndefined(value) })
        }
      />
    </>
  )
}

function ProductFilter({
  filters,
  onChange,
}: {
  filters: InventoryReportFilters
  onChange: (filters: InventoryReportFilters) => void
}) {
  return (
    <Field label="Producto">
      <CatalogSelect
        catalog="report-inventory-products"
        placeholder="Todos los productos"
        value={filters.product_id}
        onChange={(value) =>
          onChange({ ...filters, product_id: numberOrUndefined(String(value)) })
        }
      />
    </Field>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}

function OptionsSelect({
  label,
  value,
  options = [],
  allowAll = true,
  onChange,
}: {
  label: string
  value: unknown
  options?: Array<{
    id?: number
    value?: string
    name?: string
    label?: string
  }>
  allowAll?: boolean
  onChange: (value: string) => void
}) {
  const selected =
    value === undefined || value === null || value === ''
      ? 'all'
      : String(value)
  return (
    <Field label={label}>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value="all">Todos</SelectItem>}
          {options.map((option) => {
            const optionValue = String(option.id ?? option.value)
            return (
              <SelectItem key={optionValue} value={optionValue}>
                {option.name ?? option.label ?? optionValue}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </Field>
  )
}

function BooleanFilter({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex min-h-10 items-end pb-2">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
        <Checkbox
          checked={checked}
          onCheckedChange={(value) => onChange(value === true)}
        />{' '}
        {label}
      </label>
    </div>
  )
}

function InventoryResults({
  type,
  filters,
  result,
  isLoading,
  isFetching,
  error,
  onPageChange,
}: {
  type: InventoryReportType
  filters: InventoryReportFilters
  result?: InventoryReportResult<Row>
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  onPageChange: (page: number) => void
}) {
  const columns =
    type === 'stocks' && filters.show_utilities
      ? [
          ...BASE_COLUMNS.stocks,
          {
            key: 'profit_percentage',
            label: 'Utilidad configurada %',
            kind: 'percentage',
          } satisfies Column,
          {
            key: 'potential_profit',
            label: 'Utilidad potencial',
            kind: 'money',
          } satisfies Column,
        ]
      : BASE_COLUMNS[type]

  if (isLoading)
    return (
      <TableSkeleton
        headers={columns.map((column) => column.label)}
        columns={columns.map(() => 'text' as const)}
        rows={10}
      />
    )
  if (error)
    return (
      <ReportState
        icon={<AlertCircle className="h-6 w-6 text-red-500" />}
        message="No fue posible cargar el reporte. Intenta nuevamente."
      />
    )
  if (!result || result.rows.length === 0)
    return (
      <ReportState message="No hay registros para los filtros aplicados." />
    )

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h3 className="font-semibold text-table-foreground">Resultados</h3>
          <p className="text-xs text-muted-foreground">
            {result.pagination.total} registros filtrados
          </p>
          {type === 'stocks' &&
            Number(result.totals.unvalued_products_count ?? 0) > 0 && (
              <p className="mt-1 text-xs text-amber-700">
                {result.totals.unvalued_products_count} productos no pudieron
                valorarse por falta de precio.
              </p>
            )}
          {type === 'kardex' && (
            <p className="mt-1 text-xs text-muted-foreground">
              Movimiento neto: {formatInteger(result.totals.net_movement)}
            </p>
          )}
          {type === 'batches' &&
            Number(result.totals.unknown_initial_batches_count ?? 0) > 0 && (
              <p className="mt-1 text-xs text-amber-700">
                {result.totals.unknown_initial_batches_count} lotes no tienen
                una existencia inicial determinable.
              </p>
            )}
        </div>
        {isFetching && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>
      <Table className="min-w-max">
        <TableHeader className="border-b border-border bg-table-header-bg text-table-header-foreground">
          <TableRow className="hover:bg-transparent">
            {columns.map((column) => (
              <TableHead
                key={column.key}
                className={`${REPORT_TABLE_HEAD_CLASS} ${isNumeric(column) ? 'text-right' : ''}`}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className="[&_tr]:border-border">
          {result.rows.map((row, index) => (
            <TableRow
              key={`${type}-${result.pagination.current_page}-${index}`}
              className="hover:bg-table-row-hover"
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={`${REPORT_TABLE_CELL_CLASS} ${isNumeric(column) ? 'text-right font-medium tabular-nums' : ''}`}
                >
                  {formatCell(row[column.key], column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter className="border-t border-border bg-table-header-bg font-semibold text-table-foreground">
          <TableRow className="hover:bg-transparent">
            {columns.map((column, index) => (
              <TableCell
                key={column.key}
                className={`${REPORT_TABLE_CELL_CLASS} font-semibold ${isNumeric(column) ? 'text-right tabular-nums' : ''}`}
              >
                {index === 0
                  ? 'TOTAL GENERAL'
                  : formatTotal(result.totals[column.key], column)}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
      <GlobalPaginator
        totalItems={result.pagination.total}
        itemsPerPage={result.pagination.per_page}
        currentPage={result.pagination.current_page}
        from={result.pagination.from ?? 0}
        to={result.pagination.to ?? 0}
        onPageChange={onPageChange}
      />
    </div>
  )
}

function ReportState({ icon, message }: { icon?: ReactNode; message: string }) {
  return (
    <div className="flex min-h-52 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
      {icon}
      <span>{message}</span>
    </div>
  )
}

function hasActiveFilters(
  type: InventoryReportType,
  filters: InventoryReportFilters,
) {
  const defaults = DEFAULT_FILTERS[type]
  const keys = new Set([...Object.keys(defaults), ...Object.keys(filters)])

  return Array.from(keys).some((key) => filters[key] !== defaults[key])
}

function isNumeric(column: Column) {
  return ['money', 'percentage', 'quantity', 'integer'].includes(
    column.kind ?? '',
  )
}

function formatCell(value: string | number | null | undefined, column: Column) {
  if (value === null || value === undefined)
    return column.kind === 'money' ? 'No disponible' : '—'
  if (column.kind === 'money')
    return Number(value).toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
    })
  if (column.kind === 'percentage') return `${Number(value).toFixed(2)}%`
  if (column.kind === 'quantity')
    return Number(value).toLocaleString('es-MX', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })
  if (column.kind === 'integer') return formatInteger(value)
  if (column.kind === 'date' || column.kind === 'date-time')
    return formatReportDate(String(value), column.kind === 'date-time')
  return String(value)
}

function formatTotal(
  value: string | number | null | undefined,
  column: Column,
) {
  return value === undefined ? '' : formatCell(value, column)
}
function formatInteger(value: string | number | null | undefined) {
  return Number(value ?? 0).toLocaleString('es-MX', {
    maximumFractionDigits: 0,
  })
}
function formatReportDate(value: string, withTime: boolean) {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/,
  )
  if (!match) return value

  const [, year, month, day, hours = '00', minutes = '00'] = match
  return withTime
    ? `${day}/${month}/${year} ${hours}:${minutes}`
    : `${day}/${month}/${year}`
}
function numberOrUndefined(value: string) {
  return value === 'all' || value === '' ? undefined : Number(value)
}
